import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import autocompleteSearch from './autocomplete';

const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;
const EMPTY_FILTERS = [];

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightTitle = (title, query) => {
  if (!query.trim()) return title;

  const parts = title.split(new RegExp(`(${escapeRegExp(query.trim())})`, 'ig'));
  return parts.map((part, index) =>
    part.toLowerCase() === query.trim().toLowerCase() ? (
      <mark key={`${part}-${index}`}>{part}</mark>
    ) : (
      part
    )
  );
};

const applyListSearch = (query, paramName) => {
  const url = new URL(window.location.href);

  if (query) {
    url.searchParams.set(paramName, query);
  } else {
    url.searchParams.delete(paramName);
  }
  url.searchParams.delete('page');
  window.location.assign(`${url.pathname}${url.search}`);
};

const MeiliSearchBar = ({
  suggestUrl,
  initialQuery = '',
  placeholder = 'Buscar',
  name = 'search',
  filterParams = EMPTY_FILTERS,
}) => {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const term = query.trim();

    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }

    if (term.length < MIN_CHARS) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);

      try {
        const url = new URL(suggestUrl, window.location.origin);
        url.searchParams.set('q', term);

        const current = new URLSearchParams(window.location.search);
        filterParams.forEach((key) => {
          if (current.get(key)) url.searchParams.set(key, current.get(key));
        });

        const response = await fetch(url.toString(), {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });

        if (!response.ok) {
          setResults([]);
          return;
        }

        const payload = await response.json();
        setResults(Array.isArray(payload) ? payload : []);
        setActiveIndex(-1);
        if (document.activeElement === inputRef.current) setOpen(true);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setResults([]);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [query, suggestUrl, filterParams]);

  const goToItem = (item) => {
    if (!item?.editUrl) return;
    window.location.assign(item.editUrl);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (activeIndex >= 0 && results[activeIndex]) {
      goToItem(results[activeIndex]);
      return;
    }
    applyListSearch(query.trim(), name);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (!open || results.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => (current <= 0 ? results.length - 1 : current - 1));
    }
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setOpen(false);
    if (initialQuery) applyListSearch('', name);
  };

  const showPanel = open && query.trim().length >= MIN_CHARS;

  return (
    <form className="article-search__form" onSubmit={handleSubmit} role="search" ref={wrapperRef}>
      <div className="article-search__field">
        <input
          id="admin-search"
          ref={inputRef}
          type="search"
          name={name}
          value={query}
          placeholder={placeholder}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={showPanel}
          aria-controls="admin-search-results"
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => query.trim().length >= MIN_CHARS && setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {loading && <span className="article-search__spinner" aria-hidden="true" />}
        {query && !loading && (
          <button type="button" className="article-search__clear" onClick={handleClear} aria-label="Limpiar búsqueda">
            ×
          </button>
        )}
      </div>

      {showPanel && (
        <div className="article-search__panel" id="admin-search-results" role="listbox">
          {loading && results.length === 0 && (
            <p className="article-search__empty">Buscando…</p>
          )}

          {!loading && results.length === 0 && (
            <p className="article-search__empty">Sin coincidencias. Enter para buscar en la lista.</p>
          )}

          {results.map((item, index) => (
            <button
              key={item.id}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              className={`article-search__option${index === activeIndex ? ' is-active' : ''}`}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => goToItem(item)}
            >
              <span className="article-search__title">{highlightTitle(item.title, query)}</span>
              {item.meta && (
                <span className={`article-search__status${item.metaClass ? ` ${item.metaClass}` : ''}`}>
                  {item.meta}
                </span>
              )}
            </button>
          ))}

          <p className="article-search__hint">Clic para editar · Enter para filtrar la lista</p>
        </div>
      )}
    </form>
  );
};

const LegacySearchBar = ({ searchData, name }) => {
  const [addSuggest, setAddSuggest] = useState(true);

  const handleSuggestions = (e) => {
    if (addSuggest) {
      autocompleteSearch(searchData, e.target, name);
      setAddSuggest(false);
    }
  };

  return (
    <input
      type="text"
      id="autoComplete"
      placeholder="Buscar"
      onClick={(e) => handleSuggestions(e)}
      autoComplete="off"
      name={name}
    />
  );
};

const SearchBar = ({ suggestUrl, searchData, name = 'search', ...rest }) => {
  if (suggestUrl) {
    return <MeiliSearchBar suggestUrl={suggestUrl} name={name} {...rest} />;
  }

  return <LegacySearchBar searchData={searchData} name={name} />;
};

MeiliSearchBar.propTypes = {
  suggestUrl: PropTypes.string.isRequired,
  initialQuery: PropTypes.string,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  filterParams: PropTypes.arrayOf(PropTypes.string),
};

LegacySearchBar.propTypes = {
  searchData: PropTypes.arrayOf(PropTypes.string).isRequired,
  name: PropTypes.string.isRequired,
};

SearchBar.propTypes = {
  suggestUrl: PropTypes.string,
  searchData: PropTypes.arrayOf(PropTypes.string),
  name: PropTypes.string,
  initialQuery: PropTypes.string,
  placeholder: PropTypes.string,
  filterParams: PropTypes.arrayOf(PropTypes.string),
};

export default SearchBar;
